/**
 * Attribution tracking utility for capturing and persisting UTM parameters, Meta identifiers,
 * referrer data, landing page, and partner ref (ref/affiliate). Manages first-touch and last-touch
 * attribution data in browser storage for marketing analytics and lead tracking. Last-touch ref
 * is preserved when a visit has no ref (does not overwrite with null).
 * Update when modifying attribution data structure, adding new tracking parameters, or changing storage schema.
 */

const STORAGE_KEY = 'deepthink_attribution';
const FBP_COOKIE_NAME = '_fbp';
const FBC_COOKIE_NAME = '_fbc';

export interface AttributionParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_adset?: string;
  utm_ad?: string;
  fbclid?: string;
  gclid?: string;
  ref?: string | null;
  referrer?: string | null;
  landing_page?: string;
  first_seen_at?: string;
  fbc?: string | null;
  fbp?: string;
}

interface StoredAttribution {
  firstTouch: AttributionParams | null;
  lastTouch: AttributionParams | null;
  updated_at: string;
}

/**
 * Reads the partner ref slug from a query string: `ref` takes precedence over
 * `affiliate`. Exported so the click beacon counts a click for exactly the slug
 * the lead will later be attributed to — if these two ever disagreed, an
 * affiliate's click count and their lead count would describe different links.
 */
export const readRefSlug = (params: URLSearchParams): string | null => {
  const refRaw = params.get('ref') || params.get('affiliate');
  return refRaw != null && String(refRaw).trim() !== ''
    ? String(refRaw).trim()
    : null;
};

/**
 * Parses attribution parameters from the current URL query string
 */
export const parseAttributionFromURL = (): AttributionParams => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    // Safely access window.location.search with fallback for edge cases
    const search = window.location?.search || '';
    const params = new URLSearchParams(search);
    const attribution: AttributionParams = {};

    // UTM parameters
    const utmParams = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_adset',
      'utm_ad',
    ] as const;
    utmParams.forEach(param => {
      const value = params.get(param);
      if (value) {
        attribution[param] = value;
      }
    });

    // Meta click ID
    const fbclid = params.get('fbclid');
    if (fbclid) {
      attribution.fbclid = fbclid;
    }

    // Google click ID
    const gclid = params.get('gclid');
    if (gclid) {
      attribution.gclid = gclid;
    }

    attribution.ref = readRefSlug(params);

    return attribution;
  } catch (error) {
    // Gracefully handle any errors (e.g., URLSearchParams not available, window.location issues)
    // Return empty object to allow app to continue functioning
    console.warn('Error parsing attribution from URL:', error);
    return {};
  }
};

/**
 * Gets the referrer from document.referrer
 */
const getReferrer = (): string | null => {
  if (typeof window === 'undefined' || !document.referrer) {
    return null;
  }

  // Filter out same-origin referrers (internal navigation)
  try {
    const referrerUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (referrerUrl.origin === currentUrl.origin) {
      return null;
    }
    return document.referrer;
  } catch {
    return document.referrer;
  }
};

/**
 * Gets the current page URL as the landing page
 */
const getLandingPage = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.href;
};

/**
 * Extracts Meta browser ID (fbp) from cookies
 */
const getMetaBrowserIds = (): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === FBP_COOKIE_NAME && value) {
        return value;
      }
    }
  } catch (error) {
    console.warn('Error reading fbp cookie:', error);
  }

  return null;
};

/**
 * Extracts Meta click ID (fbc) from cookies
 */
const getMetaClickId = (): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === FBC_COOKIE_NAME && value) {
        return value;
      }
    }
  } catch (error) {
    console.warn('Error reading fbc cookie:', error);
  }

  return null;
};

/**
 * Generates fbc parameter from fbclid if present
 * Format: fb.1.{creationTime}.{fbclid}
 * This matches Meta's official format for the _fbc cookie and Conversions API.
 */
const generateFbcFromFbclid = (fbclid: string): string | null => {
  if (!fbclid || typeof fbclid !== 'string') {
    return null;
  }

  try {
    // fbc format: fb.1.{creationTime}.{fbclid}
    // This matches Meta's official format for the _fbc cookie
    // Creation time is current timestamp in milliseconds (as per Meta's format)
    const creationTime = Date.now();
    return `fb.1.${creationTime}.${fbclid}`;
  } catch (error) {
    console.warn('Error generating fbc from fbclid:', error);
    return null;
  }
};

/**
 * Builds a complete attribution object from current page context
 */
const buildAttributionObject = (): AttributionParams => {
  const urlAttribution = parseAttributionFromURL();
  const referrer = getReferrer();
  const landingPage = getLandingPage();
  const fbp = getMetaBrowserIds();
  const fbc = getMetaClickId();

  const attribution: AttributionParams = {
    ...urlAttribution,
    referrer: referrer || null,
    landing_page: landingPage,
    first_seen_at: new Date().toISOString(),
  };

  // Prefer fbc cookie if present, fallback to generating from fbclid
  if (fbc) {
    attribution.fbc = fbc;
  } else if (attribution.fbclid) {
    attribution.fbc = generateFbcFromFbclid(attribution.fbclid);
  }

  // Add fbp if available
  if (fbp) {
    attribution.fbp = fbp;
  }

  return attribution;
};

/**
 * Persists attribution data to localStorage
 * Stores both first-touch (only on first visit) and last-touch (updated on every visit)
 */
export const persistAttribution = (
  attributionData: AttributionParams
): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    console.warn('localStorage not available, attribution not persisted');
    return;
  }

  try {
    const existing = getStoredAttribution();
    const now = new Date().toISOString();

    // First touch: only set if it doesn't exist
    const firstTouch = existing?.firstTouch || {
      ...attributionData,
      first_seen_at: attributionData.first_seen_at || now,
    };

    // Last touch: always update with current attribution.
    // Do not overwrite ref with null when this visit has no ref—keep previous last-touch ref.
    let lastTouch: AttributionParams = {
      ...attributionData,
      first_seen_at: attributionData.first_seen_at || now,
    };
    const newRef = lastTouch.ref;
    const existingLastRef = existing?.lastTouch?.ref;
    if ((newRef == null || newRef === '') && existingLastRef) {
      lastTouch = { ...lastTouch, ref: existingLastRef };
    }

    const dataToStore: StoredAttribution = {
      firstTouch,
      lastTouch,
      updated_at: now,
    };

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
  } catch (error) {
    console.error('Error persisting attribution data:', error);
  }
};

/**
 * Retrieves stored attribution data from localStorage
 */
const getStoredAttribution = (): StoredAttribution | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }
    return JSON.parse(stored) as StoredAttribution;
  } catch (error) {
    console.error('Error reading stored attribution data:', error);
    return null;
  }
};

export interface AttributionData {
  firstTouch: AttributionParams | null;
  lastTouch: AttributionParams | null;
}

/**
 * Gets attribution data for API calls
 * Returns both first-touch and last-touch attribution
 */
export const getAttributionData = (): AttributionData => {
  const stored = getStoredAttribution();
  if (!stored) {
    return {
      firstTouch: null,
      lastTouch: null,
    };
  }

  return {
    firstTouch: stored.firstTouch || null,
    lastTouch: stored.lastTouch || null,
  };
};

/**
 * Gets attribution data with fresh fbp cookie value read at call time.
 * This ensures we capture the latest _fbp cookie value even if it wasn't available
 * during page load (e.g., due to slow Meta Pixel loading or cookie blockers).
 * Follows Meta's best practice of reading fbp at conversion time.
 * Updates lastTouch.fbp with the fresh cookie value if available, while preserving
 * firstTouch.fbp to maintain first-touch attribution integrity.
 */
export const getAttributionDataWithFreshFbp = (): AttributionData => {
  // Get stored attribution data
  const attributionData = getAttributionData();

  // Read _fbp and _fbc cookies directly at call time
  const freshFbp = getMetaBrowserIds();
  const freshFbc = getMetaClickId();
  const urlAttribution = parseAttributionFromURL();

  if (!attributionData.lastTouch) {
    const fallbackAttribution = buildAttributionObject();
    return {
      firstTouch: attributionData.firstTouch || fallbackAttribution,
      lastTouch: fallbackAttribution,
    };
  }

  let updatedLastTouch = attributionData.lastTouch;
  let didUpdate = false;

  // Preserve firstTouch to maintain first-touch attribution integrity
  if (freshFbp && freshFbp !== updatedLastTouch.fbp) {
    updatedLastTouch = {
      ...updatedLastTouch,
      fbp: freshFbp,
    };
    didUpdate = true;
  }

  if (freshFbc && freshFbc !== updatedLastTouch.fbc) {
    updatedLastTouch = {
      ...updatedLastTouch,
      fbc: freshFbc,
    };
    didUpdate = true;
  } else if (!freshFbc && !updatedLastTouch.fbc && urlAttribution.fbclid) {
    const generatedFbc = generateFbcFromFbclid(urlAttribution.fbclid);
    if (generatedFbc) {
      updatedLastTouch = {
        ...updatedLastTouch,
        fbclid: updatedLastTouch.fbclid || urlAttribution.fbclid,
        fbc: generatedFbc,
      };
      didUpdate = true;
    }
  }

  if (!didUpdate) {
    return attributionData;
  }

  // Persist fresh meta IDs back to localStorage so the next call (or retry) benefits
  // from the cached value without having to re-read the cookie.
  try {
    const stored = getStoredAttribution();
    if (stored) {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...stored,
          lastTouch: updatedLastTouch,
          updated_at: new Date().toISOString(),
        })
      );
    }
  } catch {
    // Non-critical: ignore persistence errors
  }

  return {
    firstTouch: attributionData.firstTouch,
    lastTouch: updatedLastTouch,
  };
};

/**
 * Checks if this is the first visit (no stored first-touch data)
 */
export const isFirstVisit = (): boolean => {
  const stored = getStoredAttribution();
  return !stored || !stored.firstTouch;
};

/**
 * Initializes attribution tracking by parsing URL and persisting data
 * Should be called on page load to capture attribution parameters
 * If fbp cookie is not found initially, re-checks after a delay to capture it
 * once Meta Pixel has had time to set the cookie
 */
export const initializeAttribution = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const attributionData = buildAttributionObject();
  persistAttribution(attributionData);

  // If fbp or fbc weren't found initially but Meta Pixel might still be loading,
  // re-check at increasing intervals to capture them once the pixel sets the cookie.
  // Meta Pixel typically sets _fbp within 1-2 seconds of script execution.
  // This improves match quality for CAPI events.
  if (!attributionData.fbp || !attributionData.fbc) {
    const retryIntervals = [500, 2000, 5000];

    retryIntervals.forEach(delay => {
      setTimeout(() => {
        const fbp = getMetaBrowserIds();
        const fbc = getMetaClickId();
        if (!fbp && !fbc) return;

        const stored = getStoredAttribution();
        if (!stored) {
          persistAttribution({
            ...attributionData,
            ...(fbp ? { fbp } : {}),
            ...(fbc ? { fbc } : {}),
          });
          return;
        }

        // Only fill in gaps — never overwrite values that were already captured
        const lastTouchNeedsFbp = fbp && !stored.lastTouch?.fbp;
        const lastTouchNeedsFbc = fbc && !stored.lastTouch?.fbc;
        const firstTouchNeedsFbp = fbp && !stored.firstTouch?.fbp;
        const firstTouchNeedsFbc = fbc && !stored.firstTouch?.fbc;

        if (
          !lastTouchNeedsFbp &&
          !lastTouchNeedsFbc &&
          !firstTouchNeedsFbp &&
          !firstTouchNeedsFbc
        ) {
          return;
        }

        try {
          window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              firstTouch: stored.firstTouch
                ? {
                    ...stored.firstTouch,
                    ...(firstTouchNeedsFbp ? { fbp } : {}),
                    ...(firstTouchNeedsFbc ? { fbc } : {}),
                  }
                : stored.firstTouch,
              lastTouch: stored.lastTouch
                ? {
                    ...stored.lastTouch,
                    ...(lastTouchNeedsFbp ? { fbp } : {}),
                    ...(lastTouchNeedsFbc ? { fbc } : {}),
                  }
                : stored.lastTouch,
              updated_at: new Date().toISOString(),
            })
          );
        } catch (error) {
          console.error('Error updating Meta IDs in attribution data:', error);
        }
      }, delay);
    });
  }
};

/**
 * Derives campaign intent from UTM campaign parameter
 * Analyzes utm_campaign for keywords to determine campaign intent
 */
const _deriveCampaignIntent = (
  utmCampaign: string | null | undefined
): string => {
  if (!utmCampaign || typeof utmCampaign !== 'string') {
    return 'unknown';
  }

  const campaign = utmCampaign.toLowerCase().trim();

  // Check for retargeting campaigns
  if (campaign.includes('retargeting')) {
    return 'retargeting_whatsapp';
  }

  // Check for prospecting campaigns
  if (campaign.includes('prospecting')) {
    if (campaign.includes('website')) {
      return 'prospecting_website';
    }
    if (campaign.includes('instant')) {
      return 'prospecting_instant';
    }
    // If prospecting but no specific type, default to website
    return 'prospecting_website';
  }

  // Check for trial offers
  if (campaign.includes('trial')) {
    return 'trial_offer';
  }

  // Check for diagnostic offers
  if (campaign.includes('diagnostic')) {
    return 'diagnostic_offer';
  }

  // Check for promo offers
  if (campaign.includes('promo')) {
    return 'promo_offer';
  }

  // Check for form campaigns (may be used with prospecting or standalone)
  if (campaign.includes('form')) {
    // If form is mentioned with prospecting, prioritize prospecting
    if (campaign.includes('prospecting')) {
      return 'prospecting_website';
    }
    // Otherwise, treat as prospecting_website
    return 'prospecting_website';
  }

  // If no keywords match, return unknown
  return 'unknown';
};

// Ensure _deriveCampaignIntent is retained (used externally or for future use)
void _deriveCampaignIntent;

export type TrafficType = 'paid_meta' | 'organic' | 'direct' | 'other';

/**
 * Classifies traffic type based on attribution data
 * Determines if traffic is Paid Meta, Organic, or Direct
 */
export const classifyTrafficType = (
  attributionData: AttributionParams | null | undefined
): TrafficType => {
  if (!attributionData || typeof attributionData !== 'object') {
    return 'other';
  }

  const utmSource = attributionData.utm_source?.toLowerCase();
  const utmMedium = attributionData.utm_medium?.toLowerCase();
  const fbclid = attributionData.fbclid;
  const referrer = attributionData.referrer;

  // Check for Paid Meta conditions
  const isPaidSocialMedium = utmMedium === 'paid_social';
  const isMetaSource =
    utmSource != null && ['facebook', 'instagram'].includes(utmSource);
  const hasFbclid = fbclid != null && fbclid !== '';

  if (isPaidSocialMedium || isMetaSource || hasFbclid) {
    return 'paid_meta';
  }

  // Check if UTMs are absent (no utm_source or utm_medium)
  const hasUtms =
    (attributionData.utm_source || attributionData.utm_medium) != null;

  if (!hasUtms) {
    // UTMs absent - check referrer
    if (!referrer || referrer === '') {
      return 'direct';
    }

    // Check if referrer is Google (organic) or social (non-paid)
    try {
      const referrerUrl = new URL(referrer);
      const referrerHost = referrerUrl.hostname.toLowerCase();

      // Google organic search
      if (
        referrerHost.includes('google.') ||
        referrerHost.includes('google.com')
      ) {
        return 'organic';
      }

      // Social media platforms (non-paid, since we already checked for paid Meta)
      const socialDomains = [
        'facebook.com',
        'instagram.com',
        'twitter.com',
        'x.com',
        'linkedin.com',
        'reddit.com',
        'pinterest.com',
        'tiktok.com',
        'youtube.com',
      ];

      if (socialDomains.some(domain => referrerHost.includes(domain))) {
        return 'organic';
      }

      // Other search engines
      const searchEngines = [
        'bing.com',
        'yahoo.com',
        'duckduckgo.com',
        'baidu.com',
      ];

      if (searchEngines.some(domain => referrerHost.includes(domain))) {
        return 'organic';
      }
    } catch {
      // If referrer URL parsing fails, treat as organic if referrer exists
      return 'organic';
    }
  }

  // UTMs present but don't match Paid Meta - classify as other
  return 'other';
};
